import { createIcons, icons } from 'lucide';

/**
 * Strip Alpine.js attributes from elements before Lucide processes them.
 * Lucide copies all attributes from the source element to the new SVG.
 * SVG setAttribute() throws InvalidCharacterError for names like @click, :class, x-show.
 */
function stripAlpineAttributes(elements) {
  elements.forEach(el => {
    const toRemove = [];
    for (let i = 0; i < el.attributes.length; i++) {
      const name = el.attributes[i].name;
      if (name.startsWith('@') || name.startsWith('x-') || (name.startsWith(':') && name !== ':')) {
        toRemove.push(name);
      }
    }
    toRemove.forEach(name => el.removeAttribute(name));
  });
}

// Initialize Lucide icons within a container
export function initializeLucideIcons(container = document) {
  // Match the same selector Lucide uses internally: [data-lucide], not just i[data-lucide]
  const elements = container.querySelectorAll('[data-lucide]');
  if (elements.length === 0) return;

  // Only process uninitialized <i> elements (Lucide replaces <i> → <svg>)
  const unprocessed = Array.from(elements).filter(el => el.tagName === 'I');
  if (unprocessed.length === 0) return;

  stripAlpineAttributes(unprocessed);

  try {
    createIcons({
      icons,
      attrs: {
        'stroke-width': 2,
        'width': '1em',
        'height': '1em'
      },
      nameAttr: 'data-lucide',
      root: container
    });
  } catch {
    // If batch processing fails (e.g. an attribute we didn't strip), process
    // each icon individually so one bad element doesn't block the rest.
    unprocessed.forEach(el => {
      // Skip already-converted icons (batch may have partially succeeded,
      // detaching processed <i> elements from the DOM via replaceChild)
      if (el.tagName !== 'I' || !el.parentNode) return;
      const wrapper = document.createElement('span');
      wrapper.style.display = 'contents';
      el.parentNode.insertBefore(wrapper, el);
      wrapper.appendChild(el);
      try {
        createIcons({
          icons,
          attrs: { 'stroke-width': 2, 'width': '1em', 'height': '1em' },
          nameAttr: 'data-lucide',
          root: wrapper
        });
      } catch {
        // createIcons failed for this icon — leave it as-is
      }
      // Unwrap: move children (converted SVG or original <i>) back out
      while (wrapper.firstChild) {
        wrapper.parentNode.insertBefore(wrapper.firstChild, wrapper);
      }
      wrapper.remove();
    });
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeLucideIcons());
} else {
  initializeLucideIcons();
}

// Auto-initialize Lucide icons when new <i data-lucide> elements appear in the DOM.
// Handles Alpine x-if/x-html re-renders, AJAX content, and any dynamic DOM insertion.
let mutationTimer = null;
let pendingContainers = [];
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== 1) continue;
      if (node.tagName === 'I' && node.hasAttribute('data-lucide')) {
        pendingContainers.push(node.parentNode || document);
      } else if (node.querySelector && node.querySelector('i[data-lucide]')) {
        pendingContainers.push(node);
      }
    }
  }
  if (pendingContainers.length > 0) {
    // Debounce to batch multiple rapid DOM insertions
    clearTimeout(mutationTimer);
    mutationTimer = setTimeout(() => {
      const containers = pendingContainers;
      pendingContainers = [];
      containers.forEach(c => initializeLucideIcons(c));
    }, 20);
  }
});
observer.observe(document.body || document.documentElement, {
  childList: true,
  subtree: true
});

// Export for use in other modules
window.LucideIcons = {
  initialize: initializeLucideIcons
};
